// `ListPager` (rev-7, D-560): the pager under a list that is not a table: a
// card list or a feed read a cursor page at a time. It says which rows are on
// the page and moves one page back or on. A DataTable carries its own pager
// in its footer instead (`pager` / `clientPager`).
//
// It renders nothing while the list fits on one page. When a press disables
// the button that had focus (the last page reached), focus moves to the other
// button, so a keyboard reader is never dropped to the top of the document.
// i18n-free like the other primitives: every word arrives from the caller.
// Written for the messaging inbox (spec 17 §4.1, §4.2) and moved here when
// the sales review inbox needed the same control.

import { useEffect, useRef } from "react";
import { Button } from "./button";

export interface ListPagerProps {
  /** "Rows 26–50", already translated. */
  range: string;
  hasPrev: boolean;
  hasNext: boolean;
  /** True while the next page is being read. */
  busy: boolean;
  busyLabel: string;
  prevLabel: string;
  nextLabel: string;
  onPrev: () => void;
  onNext: () => void;
  /** Prefix for the four testids: `<id>`, `<id>-range`, `<id>-prev`, `<id>-next`. */
  testId: string;
  /** Classes of the bar and of its range line; the register's by default. */
  className?: string | undefined;
  rangeClassName?: string | undefined;
}

export function ListPager({
  range,
  hasPrev,
  hasNext,
  busy,
  busyLabel,
  prevLabel,
  nextLabel,
  onPrev,
  onNext,
  testId,
  className = "ds-list-pager",
  rangeClassName = "ds-list-pager-range",
}: ListPagerProps) {
  const navRef = useRef<HTMLElement>(null);
  const pressed = useRef<"prev" | "next" | null>(null);

  // After a press lands, keep focus on the pager: on the button pressed while
  // it can still be pressed, else on the other one. A busy Next is disabled
  // while its page is read, which drops focus to the document, so the move
  // waits for the read to finish.
  useEffect(() => {
    const was = pressed.current;
    if (was === null || busy) return;
    pressed.current = null;
    const active = document.activeElement;
    const kept =
      active instanceof HTMLButtonElement &&
      !active.disabled &&
      navRef.current?.contains(active) === true;
    if (kept) return;
    const want = was === "next" ? (hasNext ? "next" : "prev") : hasPrev ? "prev" : "next";
    navRef.current?.querySelector<HTMLButtonElement>(`[data-testid="${testId}-${want}"]`)?.focus();
  });

  if (!hasPrev && !hasNext) return null;
  return (
    <nav ref={navRef} className={className} aria-label={range} data-testid={testId}>
      <span className={`${rangeClassName} num`} data-testid={`${testId}-range`}>
        {range}
      </span>
      <Button
        size="sm"
        disabled={!hasPrev}
        data-testid={`${testId}-prev`}
        onClick={() => {
          pressed.current = "prev";
          onPrev();
        }}
      >
        {prevLabel}
      </Button>
      <Button
        size="sm"
        disabled={!hasNext}
        busy={busy}
        busyLabel={busyLabel}
        data-testid={`${testId}-next`}
        onClick={() => {
          pressed.current = "next";
          onNext();
        }}
      >
        {nextLabel}
      </Button>
    </nav>
  );
}
